import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { useInfo } from "@/context/InfoContext";

export const inputCls = "h-10 px-3 border border-zinc-300 bg-white text-sm outline-none focus:border-[#111111]";
export const primaryBtn = "h-10 px-5 bg-[#111111] text-white text-xs tracking-[0.15em] uppercase disabled:opacity-50 inline-flex items-center gap-2";
const DURS = Array.from({ length: 16 }, (_, i) => (i + 1) * 15);

const ServiceRow = ({ s, i, onSave, onDelete }) => {
  const [f, setF] = useState(s);
  const dirty = JSON.stringify(f) !== JSON.stringify(s);
  return (
    <li className="py-4 grid grid-cols-2 md:grid-cols-12 gap-3 items-center" data-testid={`admin-service-row-${i}`}>
      <input className={`${inputCls} col-span-2 md:col-span-4`} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={`admin-service-name-${i}`} />
      <label className="md:col-span-2 flex items-center gap-1"><input type="number" min="0" step="0.5" className={`${inputCls} w-full`} value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid={`admin-service-price-${i}`} />€</label>
      <select className={`${inputCls} md:col-span-2`} value={f.duration} onChange={(e) => setF({ ...f, duration: Number(e.target.value) })} data-testid={`admin-service-duration-${i}`}>
        {DURS.map((d) => <option key={d} value={d}>{d} min</option>)}
      </select>
      <label className="md:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="accent-[#111111]" data-testid={`admin-service-active-${i}`} /> Visible</label>
      <div className="col-span-2 md:col-span-2 flex gap-2 justify-end">
        <button disabled={!dirty} onClick={() => onSave(f)} className={primaryBtn} data-testid={`admin-service-save-${i}`}>Guardar</button>
        <button onClick={() => onDelete(s)} className="w-10 h-10 border border-zinc-300 flex items-center justify-center hover:border-red-500" data-testid={`admin-service-delete-${i}`} aria-label="Eliminar"><Trash2 className="w-4 h-4" /></button>
      </div>
    </li>
  );
};

export default function AdminServices() {
  const api = useApi();
  const { reload } = useInfo();
  const [items, setItems] = useState(null);
  const [n, setN] = useState({ name: "", price: "", duration: 30 });

  const load = useCallback(() => api("get", "/admin/services").then(setItems).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => {
    load();
  }, [load]);

  const payload = (f) => ({ name: f.name, price: Number(f.price), duration: Number(f.duration), active: f.active ?? true, order: f.order ?? 0 });
  const validate = (f) => {
    if (f.name.trim().length < 2) return "Indica un nombre";
    if (f.price === "" || isNaN(Number(f.price)) || Number(f.price) < 0) return "Precio no válido";
    return null;
  };

  const save = async (f) => {
    const v = validate(f);
    if (v) return toast.error(v);
    try {
      await api("put", `/admin/services/${f.id}`, payload(f));
      toast.success("Servicio actualizado");
      load(); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (s) => {
    if (!window.confirm(`¿Eliminar «${s.name}»? Las reservas existentes se conservan.`)) return;
    try { await api("delete", `/admin/services/${s.id}`); toast.success("Eliminado"); load(); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const create = async (e) => {
    e.preventDefault();
    const v = validate(n);
    if (v) return toast.error(v);
    try {
      await api("post", "/admin/services", payload({ ...n, order: (items?.length || 0) + 1 }));
      setN({ name: "", price: "", duration: 30 });
      toast.success("Servicio creado"); load(); reload();
    } catch (x) { toast.error(errMsg(x)); }
  };

  if (!items) return <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />;
  return (
    <div data-testid="admin-services">
      <ul className="divide-y divide-zinc-200 border-y border-zinc-200">
        {items.map((s, i) => <ServiceRow key={s.id + JSON.stringify(s)} s={s} i={i} onSave={save} onDelete={del} />)}
      </ul>
      <form onSubmit={create} className="mt-8 grid grid-cols-2 md:grid-cols-12 gap-3 items-center" data-testid="admin-service-new-form">
        <input placeholder="Nombre del nuevo servicio" className={`${inputCls} col-span-2 md:col-span-5`} value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} data-testid="admin-service-new-name" />
        <input type="number" min="0" step="0.5" placeholder="Precio €" className={`${inputCls} md:col-span-2`} value={n.price} onChange={(e) => setN({ ...n, price: e.target.value })} data-testid="admin-service-new-price" />
        <select className={`${inputCls} md:col-span-2`} value={n.duration} onChange={(e) => setN({ ...n, duration: Number(e.target.value) })} data-testid="admin-service-new-duration">
          {DURS.map((d) => <option key={d} value={d}>{d} min</option>)}
        </select>
        <button className={`${primaryBtn} col-span-2 md:col-span-3 justify-center`} data-testid="admin-service-new-submit"><Plus className="w-4 h-4" /> Añadir servicio</button>
      </form>
    </div>
  );
}
